/* The customer's job timeline — what the homeowner's portal shows:

     Quote sent → Signed → Deposit paid → Scheduled for {date} →
     Crew on the way → Work started → Completed → Final invoice → Paid

   The server builds it for a real hub link (server/src/hubscope.js
   customerTimeline — an allowlist: a step is { key, done, at, date? } and
   nothing else from the records). This is the same function for the places
   the portal renders from the app's own state instead: the sandbox and the
   Customer Hub preview in Settings. web/scripts/unit-hubtimeline.mjs runs both
   on the same records and fails if they disagree.

   Pure; no DOM. */

export type HubStep = { key: string; done: boolean; at: number | null; date?: string };

export const TIMELINE_KEYS = ["sent", "signed", "deposit", "scheduled", "enroute", "started", "completed", "invoice", "paid"];

const tms = (v: any): number => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return isFinite(v) && v > 0 ? v : 0;
  const s = String(v);
  const t = (/^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T12:00:00") : new Date(s)).getTime();
  return isFinite(t) && t > 0 ? t : 0;
};
const isDay = (s: any) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
const WO_RANK: any = { completed: 4, done: 4, in_progress: 3, scheduled: 2 };

export function timelineWorkOrder(q: any, workOrders: any[]): any {
  const mine = (workOrders || []).filter((w: any) => w && q && w.quoteId === q.id && !/^cancel/i.test(String(w.status || "")));
  mine.sort((a: any, b: any) => (WO_RANK[b.status] || 0) - (WO_RANK[a.status] || 0) || tms(b.createdAt) - tms(a.createdAt));
  return mine[0] || null;
}

export function customerTimeline(q: any, wo: any, { total = null }: { total?: number | null } = {}): HubStep[] {
  if (!q) return [];
  const st = String(q.status || "");
  const ws = String((wo && wo.status) || "");
  const hist: any[] = Array.isArray(wo && wo.history) ? wo.history : [];
  const statusAt = (re: RegExp) => hist.filter((h: any) => h && h.kind === "status" && re.test(String(h.to || ""))).map((h: any) => tms(h.at)).filter(Boolean);
  const first = (list: number[]) => (list.length ? Math.min(...list) : 0);

  const pays = [
    ...(Array.isArray(q.stripePayments) ? q.stripePayments : []).map((p: any) => ({ at: tms(p && p.at), amount: Number(p && p.amount) || 0 })),
    ...(Array.isArray(q.payments) ? q.payments : []).map((p: any) => ({ at: tms(p && (p.createdAt || p.at || p.date)), amount: Number(p && p.amount) || 0 })),
  ].filter((p) => p.amount > 0).sort((a, b) => (a.at || Infinity) - (b.at || Infinity));
  const depositPaid = Number(q.depositPaid) || 0;
  const contract = Number(total != null ? total : (q.signedTotal != null ? q.signedTotal : q.total)) || 0;

  const completedAt = tms(wo && wo.completedAt) || first(statusAt(/^(done|completed)$/));
  const completed = !!completedAt || /^(done|completed)$/.test(ws);
  const startedAt = first(statusAt(/^in_progress$/))
    || first((Array.isArray(wo && wo.timeLogs) ? wo.timeLogs : []).map((e: any) => tms(e && (e.clockInAt || e.createdAt || e.date))).filter(Boolean));
  const started = !!startedAt || ws === "in_progress" || completed;

  let paidAt = 0, run = 0;
  for (const p of pays) { run += p.amount; if (contract > 0 && run >= contract - 0.005) { paidAt = p.at; break; } }
  if (!paidAt && contract > 0 && !pays.length && depositPaid >= contract - 0.005) paidAt = tms(q.paidAt) || tms(q.wonAt);
  const paid = !!paidAt || st === "paid" || (contract > 0 && (pays.reduce((s, p) => s + p.amount, 0) || depositPaid) >= contract - 0.005);

  const firstPay = pays.find((p) => p.at) || null;
  const hasDeposit = pays.length > 0 || depositPaid > 0;
  const depositAt = firstPay ? firstPay.at : (depositPaid > 0 ? (tms(q.wonAt) || tms(q.signature && q.signature.at)) : 0);

  const sends = (Array.isArray(q.sends) ? q.sends : []).map((s: any) => tms(s && s.at)).filter(Boolean).sort((a: number, b: number) => a - b);
  const signedAt = tms(q.signature && q.signature.at);
  const signed = !!signedAt || st === "signed" || st === "paid" || hasDeposit;
  const sentAt = sends[0] || tms(q.lastSentAt);

  const booked = !!(wo && isDay(wo.scheduledStart)) && /^(scheduled|in_progress|done|completed)$/.test(ws);
  const schedAt = booked ? Math.max(0, ...hist.filter((h: any) => h && h.kind === "scheduled" && h.start === wo.scheduledStart).map((h: any) => tms(h.at))) : 0;
  const enrouteAt = tms(wo && wo.enrouteAt);
  // the final invoice: the first time the quote (an invoice by then) was sent after the work was done
  const invoiceAt = completedAt ? (sends.find((t: number) => t >= completedAt) || 0) : 0;

  const steps: HubStep[] = [];
  const add = (key: string, done: any, at: number, extra?: any) => steps.push({ key, done: !!done, at: done && at ? at : null, ...(extra || {}) });
  add("sent", sentAt || signed, sentAt);
  add("signed", signed, signedAt);
  if (!(completed && paid && firstPay && completedAt && firstPay.at >= completedAt && pays.length === 1)) add("deposit", hasDeposit, depositAt);
  add("scheduled", booked || started, schedAt, booked ? { date: wo.scheduledStart } : undefined);
  if (enrouteAt || !started) add("enroute", !!enrouteAt, enrouteAt);
  add("started", started, startedAt);
  add("completed", completed, completedAt);
  if (!(paid && (!completedAt || (paidAt && paidAt <= completedAt)) && !invoiceAt)) add("invoice", !!invoiceAt || (completed && paid), invoiceAt);
  add("paid", paid, paidAt);
  return steps;
}

/* ---- how the portal words each step ---- */
const dayLabel = (ymd: string) => new Date(ymd + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
export function stepLabel(s: HubStep): string {
  switch (s.key) {
    case "sent": return "Quote sent";
    case "signed": return "Signed";
    case "deposit": return "Deposit paid";
    case "scheduled": return s.date ? "Scheduled for " + dayLabel(s.date) : "Scheduled";
    case "enroute": return "Crew on the way";
    case "started": return "Work started";
    case "completed": return "Completed";
    case "invoice": return "Final invoice";
    case "paid": return "Paid in full";
    default: return "";
  }
}
export function stepWhen(s: HubStep, now: number = Date.now()): string {
  if (!s.at) return s.done ? "Done" : "";
  const d = new Date(s.at);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: sameYear ? undefined : "numeric" });
  // "on the way" is about the hour, not the day
  return s.key === "enroute" ? date + ", " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : date;
}
export const STEP_ICON: Record<string, string> = {
  sent: "send", signed: "pen", deposit: "dollar", scheduled: "calendar", enroute: "mappin",
  started: "bolt", completed: "target", invoice: "clipboard", paid: "dollar",
};
